import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  formatQuotedPrice,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { customerLineRef } from "@shared-core/domain/customer-line-ref";
import { formatPriceOrNotYetPriced } from "@shared-core/domain/checkout-delivery";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { extractFieldErrors } from "@shared-core/data/validation";
import { isSplitComplete } from "@shared-core/domain/version-split";
import {
  choiceTotalPence,
  isDeliveryCounted,
  linesCounted,
  optionDeliveryNote,
  type QuoteChoiceMode,
} from "@shared-core/domain/quote-delivery";
import type {
  ArtworkWrites,
  QuoteAcceptInput,
  QuoteAccepted,
  QuoteLineView,
  QuotePageView,
  VersionView,
} from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { QuantityLadder } from "../portal/QuantityLadder";
import { ArtworkUploadSlots } from "./ArtworkUploadSlots";
import { DeliveryByAddress } from "./DeliveryByAddress";
import { DeliveryLink } from "./DeliveryLink";
import { DocumentsList } from "./DocumentsList";
import { ItemsListDetail } from "./ItemsListDetail";
import { ItemSubTabs, type ItemPane } from "./ItemSubTabs";
import { NeedsYouList, type NeedsYouItem } from "./NeedsYouList";
import { NotesPanel } from "./NotesPanel";
import { PageTabs, resolvePageTab, type PageTab } from "./PageTabs";
import type { PaymentMethod } from "./PaymentMethodChoice";
import { PriceBreakdown, type PriceRow } from "./PriceBreakdown";
import { ChosenOptions } from "./ChosenOptions";
import { QuoteDecisionBar, QuoteDecisionPanel } from "./QuoteDecision";
import { SummaryPageFrame } from "./SummaryPageFrame";
import { VersionSplitInputs } from "./VersionSplitInputs";
import { SUMMARY_BESIDE_PAGE, useMediaQuery } from "./useMediaQuery";
import { useItemPageParams } from "./useItemPageParams";
import type { FetchFile } from "./useFetchedFile";
import { ATTENTION_TINT } from "./attention";
import { usePortalPageSlots } from "../portal/PortalPageSlots";

// The customer's quote page (order to invoice doc 40 §10; the customer pages
// round, Ben 5 Oct): tabs Needs you · N (quantities still to choose; a file
// we can't print, for information), Items · N (list and detail; each item has
// [Details & quantity] and, when it needs artwork, [Artwork]), Delivery,
// Price, Documents, Notes. While the quote is open the decision area
// (QuoteDecision.tsx) is shown once, never with the lines again: the total of
// the choices, what is still to choose, pay by, the PO number, Decline… and
// Accept quote › in a sticky panel beside the page, or on a phone in a slim
// bar with a short sheet (Brad, 8 Oct). Quantities are offered with none preselected, optional extras
// unticked. Files can be sent on each version now and are checked at once;
// proofs wait for the order (doc 39 §1).
//
// The body knows nothing of how it was reached: the link route and the
// signed-in portal both read the quote and hand over what each action does. A
// page that acts as nobody (the staff view-as mirror) hands over `actions`
// null and its read names no accept, decline or download URL.

export interface QuotePageActions {
  accept: (input: QuoteAcceptInput) => Promise<QuoteAccepted>;
  /** Where to go once accepted: the order's page, or the card checkout. */
  onAccepted: (accepted: QuoteAccepted) => void;
  decline: (reason: string) => Promise<void>;
  reload: () => Promise<void>;
  writes: ArtworkWrites;
  /** Where a read's API-relative URL (a page picture of a file sent for every part) is fetched from. */
  fetchFile: FetchFile;
  /** The quote PDF's address, to fetch with `fetchFile`; null when the read names none. */
  pdfUrl: string | null;
}

interface Choices {
  /** Line number → the chosen option's position. */
  chosen: Record<number, number>;
  /** The ticked optional extras, by line number. */
  extras: ReadonlySet<number>;
  /** Line number → each version's quantity as typed so far, for a line split when ordered (`null`: not given yet). */
  splits: Record<number, (number | null)[]>;
  method: PaymentMethod | null;
  po: string;
  /** Field → message: `quantity_options.{line_no}`, `version_splits.{line_no}`, `payment_method`, `customer_order_number`. */
  errors: Record<string, string>;
}

const CHOICE_FIELD = "quantity_options.";
const SPLIT_FIELD = "version_splits.";

function isIncluded(line: QuoteLineView, choices: Choices): boolean {
  return !line.is_optional || choices.extras.has(line.line_no);
}

function linesToChoose(lines: QuoteLineView[], choices: Choices) {
  return lines.filter(
    (l) =>
      l.quantity_options.length > 0 &&
      isIncluded(l, choices) &&
      choices.chosen[l.line_no] === undefined,
  );
}

/** The quantity the customer chose for a line, if they have. */
function chosenQuantity(line: QuoteLineView, choices: Choices): number | null {
  return (
    line.quantity_options.find(
      (o) => o.position === choices.chosen[line.line_no],
    )?.quantity ?? null
  );
}

/** Lines split when ordered whose quantity is chosen but whose versions' quantities are not all given yet. */
function linesToSplit(lines: QuoteLineView[], choices: Choices) {
  return lines.filter((l) => {
    const total = chosenQuantity(l, choices);
    return (
      l.is_split_when_ordered === true &&
      isIncluded(l, choices) &&
      total !== null &&
      !isSplitComplete(choices.splits[l.line_no] ?? [], total)
    );
  });
}

function priceRows(
  lines: QuoteLineView[],
  choices: Choices,
  mode: QuoteChoiceMode,
): Array<PriceRow & { price_inc_vat_pence: number | null }> {
  // Doc 40 §10: the page shows the total of the current choices; the order's
  // total (worked out at Accept) is the one that counts. A delivery counts
  // only for the quantity chosen (spec 53 §3).
  return linesCounted(lines, choices, mode).map((l) => {
    const ref = customerLineRef(l.line_ref);
    const title =
      l.kind === "service" && l.spec ? `${l.title}, ${l.spec}` : l.title;
    if (mode !== "choosing" || l.quantity_options.length === 0) {
      return {
        ref,
        title,
        quantity: l.kind === "service" ? null : l.quantity,
        price_pence: l.price_pence,
        price_inc_vat_pence: l.price_inc_vat_pence,
        vat_label: l.vat_label,
        is_not_yet_priced: l.price_pence === null,
      };
    }
    const option = l.quantity_options.find(
      (o) => o.position === choices.chosen[l.line_no],
    );
    return {
      ref,
      title,
      quantity: option?.quantity ?? null,
      price_pence: option?.price_pence ?? null,
      price_inc_vat_pence: option?.price_inc_vat_pence ?? null,
      vat_label: option?.vat_label ?? l.vat_label,
    };
  });
}

function versionName(v: { label: string | null; name: string | null }) {
  return v.label === null ? null : v.name ? `${v.label} ${v.name}` : v.label;
}

function ClosedBanner({
  page,
  replacementHref,
}: {
  page: QuotePageView;
  replacementHref: (quoteNo: string) => string;
}) {
  // A draft, and a draft quote placed as an order (Ordered), are staff-only: neither is ever a customer page.
  if (
    page.status === "published" ||
    page.status === "draft" ||
    page.status === "ordered"
  )
    return null;
  const tone =
    page.status === "accepted"
      ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20"
      : "border-muted bg-muted/40";
  const explanation = {
    accepted: page.order_link_sentence
      ? `Thank you. ${page.order_link_sentence}`
      : "Thank you. Your order is on its way through.",
    declined: "You told us you don't want to go ahead with this quote.",
    expired: `This quote has expired, so its prices no longer stand. Ask ${page.published_by.name} for a fresh quote.`,
    withdrawn: page.replaced_by_quote_no
      ? "We've sent you an updated quote that replaces this one."
      : `We've withdrawn this quote. ${page.published_by.name} will be in touch, or you can reply to their email.`,
  }[page.status];
  return (
    <Card className={cn("gap-3 border p-5", tone)}>
      <div className="flex items-start gap-3">
        {page.status === "accepted" ? (
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-green-700 dark:text-green-400" />
        ) : (
          <Clock className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
        )}
        <div className="space-y-1">
          <p className="font-medium">{page.closing_label}</p>
          <p className="text-sm text-muted-foreground">{explanation}</p>
        </div>
      </div>
      {page.replaced_by_quote_no && (
        <div>
          <Button asChild variant="outline">
            <a href={replacementHref(page.replaced_by_quote_no)}>
              Open {page.replaced_by_quote_no}
              <ArrowRight className="size-4" />
            </a>
          </Button>
        </div>
      )}
    </Card>
  );
}

function QuoteItemDetail({
  line,
  isOpen,
  choices,
  onChoose,
  onSplit,
  onTickExtra,
  pane,
  onPane,
  writes,
  reload,
  fetchFile,
  onOpenDelivery,
  listsOptions,
  registerChoiceGroup,
}: {
  line: QuoteLineView;
  /** The customer is choosing towards Accept: options are offered and files taken. */
  isOpen: boolean;
  /** A read-only page of an open quote: the line's quantity options are listed, not offered. */
  listsOptions: boolean;
  choices: Choices;
  onChoose: (position: number) => void;
  onSplit: (entry: (number | null)[]) => void;
  onTickExtra: (ticked: boolean) => void;
  pane: ItemPane | null;
  onPane: (pane: ItemPane) => void;
  /** Null with no actions, when `isOpen` is false too. */
  writes: ArtworkWrites | null;
  reload: (() => Promise<void>) | null;
  fetchFile: FetchFile | null;
  onOpenDelivery: () => void;
  /** Hands the page the line's quantity choice, which its "still to make" jump scrolls to and focuses. */
  registerChoiceGroup: (lineNo: number, el: HTMLElement | null) => void;
}) {
  const slots = usePortalPageSlots();
  const versions = line.artwork;
  const ref = customerLineRef(line.line_ref);
  const hasOptions = isOpen && line.quantity_options.length > 0;
  const chosen = choices.chosen[line.line_no];
  const chosenOption = line.quantity_options.find((o) => o.position === chosen);
  const error = choices.errors[`${CHOICE_FIELD}${line.line_no}`];
  const errorId = `choice-${ref}-error`;
  const isService = line.kind === "service";
  const hasArtwork =
    isOpen &&
    writes !== null &&
    reload !== null &&
    versions !== null &&
    versions.length > 0;
  const artworkNeeded = hasArtwork
    ? versions.filter((v) => v.stage === "cant_print").length
    : 0;
  const current: ItemPane = hasArtwork ? (pane ?? "details") : "details";
  const isExtraTicked = choices.extras.has(line.line_no);
  const isChoiceOwed =
    hasOptions && chosen === undefined && (!line.is_optional || isExtraTicked);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold">
              <span className="text-muted-foreground tabular-nums">{ref}</span>{" "}
              {line.title}
            </h3>
            {line.is_optional && (
              <Badge variant="neutral">Optional extra</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{line.spec}</p>
          <ChosenOptions options={line.chosen_options} />
        </div>
        {!hasOptions && (
          <p className="shrink-0 text-right font-medium tabular-nums">
            {formatPriceOrNotYetPriced(line.price_pence)}
          </p>
        )}
      </div>

      <ItemSubTabs
        current={current}
        artworkNeeded={artworkNeeded}
        detailsNeedAttention={isChoiceOwed}
        hasArtwork={hasArtwork}
        onSelect={onPane}
      />

      {current === "details" || !hasArtwork ? (
        <div className="space-y-4">
          {line.is_optional && isOpen && (
            <label className="flex items-center gap-2 rounded-md bg-muted/40 p-3 text-sm">
              <input
                type="checkbox"
                checked={isExtraTicked}
                onChange={(e) => onTickExtra(e.target.checked)}
              />
              Add the {line.title} to my order
              {!hasOptions && (
                <span className="text-muted-foreground">
                  {" "}
                  (+{formatPriceOrNotYetPriced(line.price_pence)})
                </span>
              )}
            </label>
          )}
          {hasOptions ? (
            <div
              className={cn(
                "space-y-2 rounded-md focus:ring-2 focus:ring-warning focus:outline-none",
                isChoiceOwed && cn("p-3", ATTENTION_TINT),
              )}
              ref={(el) => registerChoiceGroup(line.line_no, el)}
              tabIndex={-1}
              role="group"
              aria-label={`Quantity for ${ref} ${line.title}`}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            >
              <p className="text-sm font-medium">Choose a quantity</p>
              <QuantityLadder
                rows={line.quantity_options.map((o) => ({
                  quantity: o.quantity,
                  available: true,
                  is_best_value: false,
                  is_selected: o.position === chosen,
                  total_pence: o.price_pence,
                  unit_price_pence: o.price_pence / o.quantity,
                  saving_pct: null,
                }))}
                quantities={line.quantity_options.map((o) => o.quantity)}
                selectedQuantity={chosenOption?.quantity ?? null}
                deliveryNotes={
                  new Map(
                    line.quantity_options.flatMap((o) => {
                      const note = optionDeliveryNote(o);
                      return note === null ? [] : [[o.quantity, note] as const];
                    }),
                  )
                }
                onSelect={(qty) => {
                  const option = line.quantity_options.find(
                    (o) => o.quantity === qty,
                  );
                  if (option) onChoose(option.position);
                }}
              />
              {error && (
                <p
                  id={errorId}
                  role="alert"
                  className="text-sm text-red-700 dark:text-red-400"
                >
                  {error}
                </p>
              )}
            </div>
          ) : null}
          {hasOptions && line.is_split_when_ordered && chosenOption ? (
            <VersionSplitInputs
              line={line}
              total={chosenOption.quantity}
              entry={choices.splits[line.line_no] ?? []}
              error={choices.errors[`${SPLIT_FIELD}${line.line_no}`]}
              onChange={onSplit}
            />
          ) : null}
          <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            {!hasOptions && !isService && (
              <>
                <dt className="text-muted-foreground">Quantity</dt>
                <dd>{line.quantity.toLocaleString("en-GB")}</dd>
              </>
            )}
            {line.versions.length > 1 && line.is_split_when_ordered && (
              <>
                <dt className="text-muted-foreground">Versions</dt>
                <dd>
                  {line.versions.length} versions:{" "}
                  {line.versions
                    .map(
                      (v, i) => versionName(v) ?? String.fromCharCode(65 + i),
                    )
                    .join(", ")}
                </dd>
              </>
            )}
            {line.versions.length > 1 &&
              !line.is_split_when_ordered &&
              line.versions_note && (
                <>
                  <dt className="text-muted-foreground">Versions</dt>
                  <dd>{line.versions_note}</dd>
                </>
              )}
            {line.versions.length > 1 &&
              !line.is_split_when_ordered &&
              !line.versions_note && (
                <>
                  <dt className="text-muted-foreground">Versions</dt>
                  <dd>
                    {line.versions.length}
                    {hasOptions ? ", split by quantity: " : ", split "}
                    {line.versions
                      .map(
                        (v) =>
                          `${versionName(v) ?? ""}${hasOptions || v.quantity === null ? "" : ` ${v.quantity.toLocaleString("en-GB")}`}`,
                      )
                      .join(" · ")}
                  </dd>
                </>
              )}
            {listsOptions && line.quantity_options.length > 0 && (
              <>
                <dt className="text-muted-foreground">Quantities</dt>
                <dd>
                  {line.quantity_options
                    .map(
                      (o) =>
                        `${o.quantity.toLocaleString("en-GB")}: ${formatQuotedPrice(o.price_pence)}`,
                    )
                    .join(" · ")}
                </dd>
              </>
            )}
            {line.rule_sentence && (
              <>
                <dt className="text-muted-foreground">Arrival</dt>
                <dd>{line.rule_sentence}</dd>
              </>
            )}
            {line.approve_by_sentence && (
              <>
                <dt className="text-muted-foreground">Approve by</dt>
                <dd>{line.approve_by_sentence}</dd>
              </>
            )}
          </dl>
          <DeliveryLink
            addressNames={line.versions.flatMap((v) =>
              v.deliveries.map((d) => d.address_name ?? ""),
            )}
            onOpen={onOpenDelivery}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Files are optional now; you can add them after you accept.
          </p>
          {versions.map((v) => (
            <div
              key={v.version_id}
              id={`artwork-${v.version_id}`}
              className="space-y-2 rounded-lg border p-3"
            >
              <p className="text-sm font-medium">
                {v.display
                  ? `Version ${v.display}`
                  : v.slots.length > 1
                    ? "Your files"
                    : "Your file"}
              </p>
              <ArtworkUploadSlots
                version={v}
                writes={writes}
                onChanged={reload}
                fetchFile={fetchFile}
                isFileOptional
                idleHint="Files are optional now."
              />
            </div>
          ))}
        </div>
      )}
      {slots.quoteLine?.(line.line_ref)}
    </div>
  );
}

function lineStatus(
  line: QuoteLineView,
  isOpen: boolean,
  choices: Choices,
): { status: string; hasAttention: boolean } {
  const cantPrint = (line.artwork ?? []).some(
    (v: VersionView) => v.stage === "cant_print",
  );
  const parts: string[] = [];
  let attention = false;
  if (isOpen && line.quantity_options.length > 0) {
    const option = line.quantity_options.find(
      (o) => o.position === choices.chosen[line.line_no],
    );
    if (option) {
      parts.push(
        `${option.quantity.toLocaleString("en-GB")} · ${formatQuotedPrice(option.price_pence)}`,
      );
      if (
        line.is_split_when_ordered &&
        !isSplitComplete(choices.splits[line.line_no] ?? [], option.quantity)
      ) {
        parts.push("Give each version's quantity");
        attention = isIncluded(line, choices);
      }
    } else {
      parts.push("Choose a quantity");
      attention = isIncluded(line, choices);
    }
  } else if (line.is_optional && isOpen) {
    parts.push(
      `${choices.extras.has(line.line_no) ? "Added" : "Optional extra"} · ${formatPriceOrNotYetPriced(line.price_pence)}`,
    );
  } else if (line.kind === "service") {
    parts.push(formatPriceOrNotYetPriced(line.price_pence));
  } else {
    parts.push(
      `${line.quantity.toLocaleString("en-GB")} · ${formatPriceOrNotYetPriced(line.price_pence)}`,
    );
  }
  if (line.versions.length > 1) parts.push(`${line.versions.length} versions`);
  // The dot alone says a file can't print (Ben, 5 Oct): no status text for it.
  if (isOpen && cantPrint) attention = true;
  return {
    status: parts.join(" · "),
    hasAttention:
      attention ||
      Boolean(choices.errors[`${CHOICE_FIELD}${line.line_no}`]) ||
      Boolean(choices.errors[`${SPLIT_FIELD}${line.line_no}`]),
  };
}

export function QuotePageBody({
  page,
  actions,
  replacementHref,
  slot,
  backLink,
}: {
  page: QuotePageView;
  /** Null: a page that acts as nobody (the staff view-as mirror): nothing to accept, decline, upload or download. */
  actions: QuotePageActions | null;
  /** Where the quote that replaced this one opens. */
  replacementHref: (quoteNo: string) => string;
  /** A block a shell may place above the tabs. The body renders whatever it is handed and knows nothing of what or why:
   *  the customer's pages are given none. */
  slot?: ReactNode;
  /** The shell's way back (to its Quotes list), set above the quote number in the page's header. */
  backLink?: ReactNode;
}) {
  const [choices, setChoicesState] = useState<Choices>({
    chosen: {},
    extras: new Set(),
    splits: {},
    method: null,
    po: "",
    errors: {},
  });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const { searchParams, setParams, selectItem } = useItemPageParams();
  const setChoices = (patch: Partial<Choices>) =>
    setChoicesState((c) => ({ ...c, ...patch }));

  const isOpen = page.status === "published";
  // Choosing quantities and extras only leads to Accept: a read-only page lists them instead.
  const isChoosing = isOpen && actions !== null;
  const accept = page.accept ?? null;
  // The tab a link with none lands on is fixed when the page loads, so
  // finishing the last choice doesn't move the customer off Needs you.
  const [landingTab] = useState<PageTab>(() =>
    resolvePageTab(
      null,
      isOpen && accept
        ? linesToChoose(page.lines, {
            chosen: {},
            extras: new Set(),
            splits: {},
            method: null,
            po: "",
            errors: {},
          }).length
        : 0,
    ),
  );
  useEffect(() => setFailure(null), [page.status]);

  const mode: QuoteChoiceMode = isChoosing
    ? "choosing"
    : isOpen
      ? "listing"
      : "decided";
  const rows = priceRows(page.lines, choices, mode);
  const missing = isChoosing ? linesToChoose(page.lines, choices) : [];
  const unsplit = isChoosing ? linesToSplit(page.lines, choices) : [];
  const total = choiceTotalPence(page.lines, choices, mode);
  // A delivery not yet priced is in no total, so it does not hold up the figure including VAT either.
  const pricedRows = rows.filter((r) => !r.is_not_yet_priced);
  const totalIncVat =
    missing.length > 0 || pricedRows.some((r) => r.price_inc_vat_pence === null)
      ? null
      : pricedRows.reduce((sum, r) => sum + (r.price_inc_vat_pence ?? 0), 0);
  const pane = searchParams.get("view") as ItemPane | null;
  const itemKey = searchParams.get("item");
  const hasDecision = isOpen && accept !== null;

  // Going to a line's choice: open it, then scroll to it and focus it (a ring shows where), once it is on screen.
  const [jumpToLineNo, setJumpToLineNo] = useState<number | null>(null);
  const choiceGroups = useRef(new Map<number, HTMLElement>());
  const registerChoiceGroup = (lineNo: number, el: HTMLElement | null) => {
    if (el) choiceGroups.current.set(lineNo, el);
    else choiceGroups.current.delete(lineNo);
  };
  const decisionPanelRef = useRef<HTMLElement>(null);
  const goToLine = (lineNo: number) => {
    const line = page.lines.find((l) => l.line_no === lineNo);
    if (!line) return;
    setParams({
      tab: "items",
      item: customerLineRef(line.line_ref),
      view: "details",
    });
    setJumpToLineNo(lineNo);
  };
  useEffect(() => {
    if (jumpToLineNo === null) return;
    const line = page.lines.find((l) => l.line_no === jumpToLineNo);
    const group = line && choiceGroups.current.get(line.line_no);
    if (!group) return; // not rendered yet: runs again when the URL has moved on
    group.scrollIntoView({ block: "center" });
    group.focus({ preventScroll: true });
    setJumpToLineNo(null);
  }, [jumpToLineNo, page.lines, searchParams]);
  const isSummaryBesidePage = useMediaQuery(SUMMARY_BESIDE_PAGE);

  async function acceptQuote() {
    setBusy(true);
    setFailure(null);
    setChoices({ errors: {} });
    // Only a split that is whole goes: a line without one is refused on its own inputs, in the backend's words.
    const sentSplits = page.lines.flatMap((l) => {
      const total = chosenQuantity(l, choices);
      const entry = choices.splits[l.line_no] ?? [];
      return l.is_split_when_ordered === true &&
        isIncluded(l, choices) &&
        total !== null &&
        isSplitComplete(entry, total)
        ? [{ line_no: l.line_no, quantities: entry as number[] }]
        : [];
    });
    try {
      if (actions === null) throw new Error("This page is read-only.");
      const accepted = await actions.accept({
        quote_no: page.quote_no,
        payment_method: choices.method,
        customer_order_number: choices.po,
        quantity_options: Object.entries(choices.chosen).map(
          ([lineNo, position]) => ({ line_no: Number(lineNo), position }),
        ),
        included_extras: [...choices.extras],
        version_splits: sentSplits.map(({ line_no, quantities }) => ({
          line_no,
          quantities,
        })),
      });
      actions.onAccepted(accepted);
    } catch (err) {
      setBusy(false);
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const errors: Record<string, string> = {};
        for (const e of fieldErrors) {
          if (e.field === "quantity_options" && e.code === "required") {
            // Quotes names the missing choice by the line as the customer
            // reads it; bind it to every line still without one.
            for (const l of linesToChoose(page.lines, choices)) {
              errors[`${CHOICE_FIELD}${l.line_no}`] =
                `Choose a quantity for ${customerLineRef(l.line_ref)}`;
            }
          } else if (e.field === "version_splits" && e.code === "required") {
            for (const l of linesToSplit(page.lines, choices)) {
              errors[`${SPLIT_FIELD}${l.line_no}`] =
                `Give each version's quantity for ${customerLineRef(l.line_ref)}.`;
            }
          } else if (e.field.startsWith(SPLIT_FIELD)) {
            // `version_splits.{i}.quantities…` names the i-th split sent: bind it to that line's inputs.
            const index = Number(
              e.field.slice(SPLIT_FIELD.length).split(".")[0],
            );
            const sent = sentSplits[index];
            errors[`${SPLIT_FIELD}${sent ? sent.line_no : "form"}`] = e.message;
          } else {
            errors[e.field] = e.message;
          }
        }
        setChoices({ errors });
      } else {
        setFailure(
          mutationErrorMessage(
            err,
            "We couldn't accept this quote. Please try again.",
          ),
        );
      }
    }
  }

  async function declineQuote(reason: string) {
    setBusy(true);
    try {
      if (actions === null) throw new Error("This page is read-only.");
      await actions.decline(reason);
      setBusy(false);
      await actions.reload();
    } catch (err) {
      setBusy(false);
      setFailure(
        mutationErrorMessage(err, "We couldn't record that. Please try again."),
      );
    }
  }

  // Needs you on a quote (Ben, 5 Oct): quantities still to choose. Each row
  // stays once shown, ticked when done, so a field being typed into doesn't
  // vanish under the customer; only rows not done count. How to pay and the
  // PO number live only in the Accept bar.
  const needs: NeedsYouItem[] = [];
  if (isOpen && accept) {
    for (const l of page.lines.filter(
      (x) => x.quantity_options.length > 0 && isIncluded(x, choices),
    )) {
      const prices = l.quantity_options.map((o) => o.price_pence);
      const option = l.quantity_options.find(
        (o) => o.position === choices.chosen[l.line_no],
      );
      needs.push({
        key: `choose-${l.line_no}`,
        icon: "choice",
        isDone: option !== undefined,
        title: `${customerLineRef(l.line_ref)} ${l.title}: choose a quantity`,
        why: option
          ? `You chose ${option.quantity.toLocaleString("en-GB")} for ${formatQuotedPrice(option.price_pence)}.`
          : `${l.quantity_options.length} quantities, from ${formatQuotedPrice(Math.min(...prices))} to ${formatQuotedPrice(Math.max(...prices))}.`,
        action: (
          <Button
            size="sm"
            variant="outline"
            onClick={() => goToLine(l.line_no)}
          >
            Choose ›
          </Button>
        ),
      });
    }
    for (const l of page.lines.filter(
      (x) => x.is_split_when_ordered === true && isIncluded(x, choices),
    )) {
      const total = chosenQuantity(l, choices);
      if (total === null) continue;
      const isDone = isSplitComplete(choices.splits[l.line_no] ?? [], total);
      needs.push({
        key: `split-${l.line_no}`,
        icon: "choice",
        isDone,
        title: `${customerLineRef(l.line_ref)} ${l.title}: give each version's quantity`,
        why: isDone
          ? `You gave each of the ${l.versions.length} versions its quantity.`
          : `${l.versions.length} versions share your ${total.toLocaleString("en-GB")}.`,
        action: (
          <Button
            size="sm"
            variant="outline"
            onClick={() => goToLine(l.line_no)}
          >
            Give ›
          </Button>
        ),
      });
    }
    // A file we can't print, for information: files are optional before
    // acceptance, so it doesn't count (Ben, 5 Oct).
    for (const l of page.lines) {
      for (const v of l.artwork ?? []) {
        if (v.stage !== "cant_print") continue;
        const ref = customerLineRef(l.line_ref);
        needs.push({
          key: `cant-${v.version_id}`,
          icon: "cant_print",
          isInfo: true,
          title: `${ref} ${l.title}${v.display ? ` · ${v.display}` : ""}: we can't print the file you sent`,
          why: "Files are optional now: send a new one, remove it, or leave it until you've accepted.",
          action: (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setParams({ tab: "items", item: ref, view: "artwork" })
              }
            >
              See why ›
            </Button>
          ),
        });
      }
    }
  }

  const openNeeds = needs.filter((n) => !n.isDone && !n.isInfo).length;
  const requestedTab = searchParams.get("tab");
  const tab = requestedTab ? resolvePageTab(requestedTab, 0) : landingTab;

  // Line types (Ben, 5 Oct): items (optional extras included) are listed;
  // service lines (delivery, set-up) are not: they show on Price and, for
  // delivery, on the Delivery tab. Refs stay as they are.
  const itemLines = page.lines.filter((l) => l.kind === "item");
  const deliveryCharges = page.lines
    .filter((l) => l.is_delivery_charge && isDeliveryCounted(l, choices, mode))
    .map((l) => ({
      ref: customerLineRef(l.line_ref),
      title: l.title,
      spec: l.spec,
      price_pence: l.price_pence,
      vat_label: l.vat_label,
      delivers_to: l.delivers_to ?? null,
    }));
  const listItems = itemLines.map((l) => {
    const { status, hasAttention } = lineStatus(l, isChoosing, choices);
    const ref = customerLineRef(l.line_ref);
    return { key: ref, ref, title: l.title, status, hasAttention };
  });

  const itemDetail = (key: string) => {
    const line = page.lines.find((l) => customerLineRef(l.line_ref) === key);
    if (!line) return null;
    return (
      <QuoteItemDetail
        key={key}
        line={line}
        isOpen={isChoosing}
        listsOptions={isOpen && !isChoosing}
        registerChoiceGroup={registerChoiceGroup}
        choices={choices}
        pane={pane}
        onPane={(p) => setParams({ view: p })}
        onChoose={(position) => {
          const { [`${CHOICE_FIELD}${line.line_no}`]: _, ...rest } =
            choices.errors;
          // A different quantity is a different total: the split given for the last one is dropped.
          const { [line.line_no]: __, ...splits } = choices.splits;
          setChoices({
            chosen: { ...choices.chosen, [line.line_no]: position },
            splits,
            errors: rest,
          });
        }}
        onSplit={(entry) => {
          const { [`${SPLIT_FIELD}${line.line_no}`]: _, ...rest } =
            choices.errors;
          setChoices({
            splits: { ...choices.splits, [line.line_no]: entry },
            errors: rest,
          });
        }}
        onTickExtra={(ticked) => {
          const next = new Set(choices.extras);
          if (ticked) next.add(line.line_no);
          else next.delete(line.line_no);
          setChoices({ extras: next });
        }}
        writes={actions?.writes ?? null}
        reload={actions?.reload ?? null}
        fetchFile={actions?.fetchFile ?? null}
        onOpenDelivery={() =>
          setParams({ tab: "delivery", item: null, view: null })
        }
      />
    );
  };

  const decisionProps = {
    page,
    totalPence: total,
    totalIncVatPence: totalIncVat,
    outstanding: missing.map((l) => ({ lineNo: l.line_no })),
    splitsOutstanding: unsplit.map((l) => ({ lineNo: l.line_no })),
    choices,
    setChoices,
    busy,
    failure,
    onAccept: () => void acceptQuote(),
    onDecline: (reason: string) => void declineQuote(reason),
    goToLine,
    onOpenPrice: () => setParams({ tab: "price", item: null, view: null }),
  };

  return (
    <SummaryPageFrame
      hasSummary={hasDecision}
      isSummaryBesidePage={isSummaryBesidePage}
      header={
        <>
          {backLink}
          {hasDecision && isSummaryBesidePage && (
            <button
              type="button"
              className="sr-only focus:not-sr-only focus:rounded-md focus:border focus:bg-card focus:px-3 focus:py-1.5 focus:text-sm"
              onClick={() => decisionPanelRef.current?.focus()}
            >
              Skip to your total and accepting
            </button>
          )}
          <div className="space-y-2">
            <p className="font-mono text-xs text-muted-foreground">
              {page.quote_no}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {page.title}
              </h1>
              {!isOpen && <Badge variant="neutral">{page.status_label}</Badge>}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>
                For{" "}
                <span className="font-medium text-foreground">
                  {page.customer_name}
                </span>
              </span>
              {isOpen && (
                <span>
                  Valid until{" "}
                  <span className="font-medium text-foreground">
                    {formatWeekdayDateOrDash(page.expires_at)}
                  </span>
                </span>
              )}
              <span>
                Quote date {formatWeekdayDateOrDash(page.published_at)} · from{" "}
                {page.published_by.name}, Pollards
              </span>
              {page.required_on && (
                <span>
                  You need it by {formatWeekdayDateOrDash(page.required_on)}
                </span>
              )}
              {page.replaces_quote_no && (
                <span>Replaces {page.replaces_quote_no}</span>
              )}
            </div>
            {page.choices_sentence && (
              <p className="text-sm text-muted-foreground">
                {page.choices_sentence}
              </p>
            )}
          </div>

          <ClosedBanner page={page} replacementHref={replacementHref} />

          {slot}
        </>
      }
      panel={
        <QuoteDecisionPanel {...decisionProps} panelRef={decisionPanelRef} />
      }
      bar={<QuoteDecisionBar {...decisionProps} />}
    >
      <PageTabs
        label="Quote"
        current={tab}
        needsCount={openNeeds}
        itemsCount={itemLines.length}
        itemsNeedAttention={listItems.some((i) => i.hasAttention)}
        onSelect={(t) => setParams({ tab: t, item: null, view: null })}
      />

      <div role="tabpanel">
        {tab === "needs" && (
          <NeedsYouList
            rows={needs}
            emptySentence={
              isOpen
                ? "Nothing more needed: you can accept the quote."
                : "Nothing needed from you."
            }
          />
        )}
        {tab === "items" && (
          <ItemsListDetail
            items={listItems}
            selectedKey={itemKey}
            defaultKey={
              listItems.find((i) => i.hasAttention)?.key ??
              listItems[0]?.key ??
              null
            }
            onSelect={(key) => selectItem(key, { view: null })}
            backLabel="All items"
            renderDetail={itemDetail}
          />
        )}
        {tab === "delivery" && (
          <DeliveryByAddress
            groups={page.delivery_groups}
            charges={deliveryCharges}
            sentence={
              page.required_on
                ? `You need it by ${formatWeekdayDateOrDash(page.required_on)}`
                : null
            }
          />
        )}
        {tab === "price" && (
          <Card className="gap-3 p-5">
            <PriceBreakdown
              rows={rows}
              totalPence={
                isChoosing
                  ? missing.length > 0
                    ? null
                    : total
                  : page.total_pence
              }
              totalIncVatPence={totalIncVat}
              missingHint={
                missing.length > 0
                  ? `Choose a quantity for ${missing.map((l) => customerLineRef(l.line_ref)).join(" and ")} to see your total.`
                  : null
              }
            />
          </Card>
        )}
        {tab === "documents" && (
          <DocumentsList
            documents={[
              {
                key: "quote",
                kind: "Quote",
                label: `${page.quote_no}, ${page.title}`,
                dated: page.published_at,
                url: actions?.pdfUrl ?? null,
              },
            ]}
            emptySentence=""
            fetchFile={actions?.fetchFile ?? null}
          />
        )}
        {tab === "notes" && (
          <NotesPanel
            messages={
              page.custom_note
                ? [
                    {
                      key: "note",
                      from: `${page.published_by.name}, Pollards`,
                      at: page.published_at,
                      text: page.custom_note,
                    },
                  ]
                : []
            }
            questionsHeading="Questions about this quote?"
            contact={page.published_by}
            contactLine={`${page.published_by.name} put it together for you.`}
          />
        )}
      </div>
    </SummaryPageFrame>
  );
}
